import { I18n, type Scope, type TranslateOptions } from "i18n-js";
import type { CircleLocale } from "@circle-react/types/CircleLocale";
import enTranslation from "./en.json";
import TimeAgo from "javascript-time-ago";
import en from "javascript-time-ago/locale/en";

const isDevelopment = process.env.NODE_ENV === "development";

const I18nInstance = new I18n();

// In development mode, we DON'T load English immediately to simulate the lazy-load delay
I18nInstance.store(isDevelopment ? {} : { en: enTranslation.en });
I18nInstance.enableFallback = true;
I18nInstance.defaultLocale = "en";
I18nInstance.locale = window.locale ?? "en";

TimeAgo.addDefaultLocale(en);

if (isDevelopment) {
  I18nInstance.missingBehavior = "missing";
  I18nInstance.missingTranslation.register(
    "missing",
    (_i18n, scope) => `[🟡 MISSING: ${String(scope)}]`,
  );
}


let isInitialized = false;
let initPromise: Promise<void> | null = null;

export const loadTranslations = async (): Promise<void> => {
  if (isInitialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    const locale: CircleLocale = window.locale ?? "en";

    // ============================================================
    // DEV MODE: Lazy-Load Simulation
    // Simulates the async loading delay even for English locale
    // to catch race conditions that would affect non-English users
    // ============================================================
    if (isDevelopment && locale === "en") {
      await new Promise(resolve => setTimeout(resolve, 0));
      I18nInstance.store({ en: enTranslation.en });
    } else if (locale === "pt") {
      const [pt, timeAgoPt] = await Promise.all([
        import(/* webpackChunkName: 'i18n_pt' */ "./pt.json"),
        import(/* webpackChunkName: 'time_ago_pt' */ "javascript-time-ago/locale/pt"),
      ]);
      I18nInstance.store({ pt: pt.default.pt });
      TimeAgo.addLocale(timeAgoPt.default);
    } else if (locale === "pt-PT") {
      const [ptPT, timeAgoPtPT] = await Promise.all([
        import(/* webpackChunkName: 'i18n_pt_pt' */ "./pt-PT.json"),
        import(
          /* webpackChunkName: 'time_ago_pt_pt' */
          "javascript-time-ago/locale/pt-PT"
        ),
      ]);
      I18nInstance.store({ "pt-PT": ptPT.default["pt-PT"] });
      TimeAgo.addLocale(timeAgoPtPT.default);
    } else if (locale === "es") {
      const [es, timeAgoEs] = await Promise.all([
        import(/* webpackChunkName: 'i18n_es' */ "./es.json"),
        import(/* webpackChunkName: 'time_ago_es' */ "javascript-time-ago/locale/es"),
      ]);
      I18nInstance.store({ es: es.default.es });
      TimeAgo.addLocale(timeAgoEs.default);
    } else if (locale === "fr") {
      const [fr, timeAgoFr] = await Promise.all([
        import(/* webpackChunkName: 'i18n_fr' */ "./fr.json"),
        import(/* webpackChunkName: 'time_ago_fr' */ "javascript-time-ago/locale/fr"),
      ]);
      I18nInstance.store({ fr: fr.default.fr });
      TimeAgo.addLocale(timeAgoFr.default);
    } else if (locale === "it") {
      const [it, timeAgoIt] = await Promise.all([
        import(/* webpackChunkName: 'i18n_it' */ "./it.json"),
        import(/* webpackChunkName: 'time_ago_it' */ "javascript-time-ago/locale/it"),
      ]);
      I18nInstance.store({ it: it.default.it });
      TimeAgo.addLocale(timeAgoIt.default);
    } else if (locale === "de") {
      const [de, timeAgoDe] = await Promise.all([
        import(/* webpackChunkName: 'i18n_de' */ "./de.json"),
        import(/* webpackChunkName: 'time_ago_de' */ "javascript-time-ago/locale/de"),
      ]);
      I18nInstance.store({ de: de.default.de });
      TimeAgo.addLocale(timeAgoDe.default);
    }

    isInitialized = true;
  })();

  return initPromise;
};

// Preserve the v3 API surface across call sites:
// - Pin the return to `string`. The v4 `t` is generic (`<T = string>(...) =>
//   string | T`), which widens to `string | undefined` when bound directly.
// - Keep `count` permissive (`string | number | null`). v4 types it as
//   `number`, but existing call sites pass localized strings (e.g.
//   `toLocaleString(...)`) and nullable counts.
type TOptions = Omit<TranslateOptions, "count"> & {
  count?: string | number | null;
};
export const t = (scope: Scope, options?: TOptions): string =>
  I18nInstance.t<string>(scope, options as TranslateOptions);
export const toHumanSize = I18nInstance.numberToHumanSize.bind(I18nInstance);

export default I18nInstance;
